/* Progressive enhancement only. No cart, variant, navigation or checkout handlers. */
import { getIntersectionRoot, getScrollContainer, getScrollEventTarget, scrollContainerMediaQuery } from '@theme/scroll-container';

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

// One shared observer, no scroll handler. Elements stay visible if JS is unavailable.
const revealSelector = '[data-seven-reveal], product-card, .collection-card, .text-block, .image-block, .featured-product-section, .section-resource-list__content, .seven-section-heading, .seven-editorial__main, .seven-editorial__copy, .seven-garment__copy, .seven-garment__stage, .seven-community__media, .seven-statement__bottom, .seven-signup__form';
const revealSeen = new WeakSet();
const revealPending = new Set();
let revealObserver;
function observeReveals(scope = document) {
  if (!revealObserver) return;
  const nodes = [...(scope.matches?.(revealSelector) ? [scope] : []), ...scope.querySelectorAll(revealSelector)];
  for (const node of nodes) {
    if (revealSeen.has(node) || node.closest('.seven-hero, header, dialog, [role="dialog"]')) continue;
    // Animate each card or content group once, without nested text animations.
    if (node.parentElement?.closest(revealSelector)) continue;
    revealSeen.add(node);
    if (reducedMotion.matches) continue;
    if (node.matches('product-card')) {
      const cell = node.closest('.seven-product-cell, .product-grid__item, .resource-list__item') || node;
      const index = Array.prototype.indexOf.call(cell.parentElement.children, cell);
      node.style.setProperty('--seven-delay', `${(index % 4) * 35}ms`);
    }
    revealPending.add(node);
    revealObserver.observe(node);
  }
}
function resetRevealObserver() {
  revealObserver?.disconnect();
  if (!('IntersectionObserver' in window)) return;
  revealObserver = new IntersectionObserver(entries => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      if (!reducedMotion.matches && !entry.target.contains(document.activeElement)) entry.target.classList.add('seven-revealed');
      revealPending.delete(entry.target);
      revealObserver.unobserve(entry.target);
    }
  }, { root: getIntersectionRoot(), threshold: 0.08 });
  for (const node of revealPending) {
    if (node.isConnected && !reducedMotion.matches) revealObserver.observe(node);
    else revealPending.delete(node);
  }
  observeReveals();
}
resetRevealObserver();
scrollContainerMediaQuery.addEventListener('change', resetRevealObserver);
reducedMotion.addEventListener('change', resetRevealObserver);
document.addEventListener('shopify:section:load', event => observeReveals(event.target));
const revealMain = document.querySelector('main');
if (revealMain) new MutationObserver(records => {
  for (const record of records) for (const node of record.addedNodes) {
    if (node.nodeType === Node.ELEMENT_NODE) observeReveals(node);
  }
  for (const node of revealPending) if (!node.isConnected) {
    revealObserver?.unobserve(node);
    revealPending.delete(node);
  }
}).observe(revealMain, { childList: true, subtree: true });

class SevenMotion extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    const video = this.querySelector('[data-seven-video] video');
    const videoToggle = this.querySelector('[data-video-toggle]');
    if (video && videoToggle) {
      videoToggle.hidden = false;
      const updateVideoButton = () => {
        videoToggle.setAttribute('aria-pressed', String(!video.paused));
        videoToggle.textContent = video.paused ? 'PLAY BACKGROUND VIDEO' : 'PAUSE BACKGROUND VIDEO';
      };
      video.addEventListener('play', updateVideoButton, { signal: this.abort.signal });
      video.addEventListener('pause', updateVideoButton, { signal: this.abort.signal });
      videoToggle.addEventListener('click', async () => {
        if (!video.paused) { video.pause(); return; }
        try { await video.play(); }
        catch { this.querySelector('[data-video-status]').textContent = 'Video unavailable. Please try again.'; }
      }, { signal: this.abort.signal });
      document.addEventListener('visibilitychange', () => { if (document.hidden) video.pause(); }, { signal: this.abort.signal });
    }
    this.depth = this.querySelector('[data-seven-depth]');
    this.onScroll = () => {
      if (!this.visible || this.frame || reducedMotion.matches) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        const rect = this.getBoundingClientRect();
        const root = getIntersectionRoot();
        const top = root ? root.getBoundingClientRect().top : 0;
        const movement = Math.max(-22, Math.min(22, (top - rect.top) * 0.055));
        this.depth.style.transform = `translate3d(0,${movement}px,0)`;
      });
    };
    this.bindDepth = () => {
      cancelAnimationFrame(this.frame);
      this.frame = null;
      this.depthAbort?.abort();
      this.depthObserver?.disconnect();
      if (!this.depth || this.dataset.parallax !== 'true') return;
      this.depth.style.removeProperty('transform');
      if (reducedMotion.matches) return;
      this.depthAbort = new AbortController();
      this.depthObserver = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting;
        this.onScroll();
      }, { root: getIntersectionRoot() });
      this.depthObserver.observe(this);
      getScrollEventTarget().addEventListener('scroll', this.onScroll, { passive: true, signal: this.depthAbort.signal });
    };
    this.bindDepth();
    scrollContainerMediaQuery.addEventListener('change', this.bindDepth, { signal: this.abort.signal });
    reducedMotion.addEventListener('change', this.bindDepth, { signal: this.abort.signal });
    // Hero anchor follows the theme's scroll container on both mobile and desktop.
    this.querySelector('a[href="#SevenDrop"]')?.addEventListener('click', (event) => {
      const target = document.getElementById('SevenDrop');
      if (!target) return;
      event.preventDefault();
      const root = getScrollContainer();
      const rootTop = getIntersectionRoot()?.getBoundingClientRect().top || 0;
      root.scrollTo({ top: root.scrollTop + target.getBoundingClientRect().top - rootTop - 80, behavior: reducedMotion.matches ? 'instant' : 'smooth' });
      target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
    }, { signal: this.abort.signal });
  }
  disconnectedCallback() {
    this.abort?.abort();
    this.depthAbort?.abort();
    this.observer?.disconnect();
    this.depthObserver?.disconnect();
    cancelAnimationFrame(this.frame);
  }
}

class SevenMarquee extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    this.querySelector('[data-marquee-toggle]')?.addEventListener('click', (event) => {
      const paused = this.toggleAttribute('data-paused');
      const button = event.currentTarget;
      button.setAttribute('aria-pressed', String(paused));
      button.setAttribute('aria-label', paused ? 'Resume marquee motion' : 'Pause marquee motion');
      button.querySelector('[data-pause-label]').textContent = paused ? '▶' : 'Ⅱ';
    }, { signal: this.abort.signal });
    this.observer = new IntersectionObserver(([entry]) => this.toggleAttribute('data-offscreen', !entry.isIntersecting));
    this.observer.observe(this);
  }
  disconnectedCallback() { this.abort?.abort(); this.observer?.disconnect(); }
}

class SevenGarment extends SevenMotion {
  connectedCallback() {
    super.connectedCallback();
    const controls = this.querySelector('.seven-garment__controls');
    if (!controls) return;
    controls.hidden = false;
    this.querySelector('[data-garment-back]').hidden = true;
    controls.addEventListener('click', (event) => {
      const button = event.target.closest('[data-view]');
      if (!button) return;
      controls.querySelectorAll('button').forEach((item) => item.setAttribute('aria-pressed', String(item === button)));
      this.querySelector('[data-garment-front]').hidden = button.dataset.view !== 'front';
      this.querySelector('[data-garment-back]').hidden = button.dataset.view !== 'back';
    }, { signal: this.abort.signal });
  }
}

if (!customElements.get('seven-motion')) customElements.define('seven-motion', SevenMotion);
if (!customElements.get('seven-marquee')) customElements.define('seven-marquee', SevenMarquee);
if (!customElements.get('seven-garment')) customElements.define('seven-garment', SevenGarment);
